import React, { useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	useActiveRightSidebarState,
	useRightSidebarController,
} from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	FireScreenAnalytics,
	SCREEN,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import type { OpenTemplatesPanelButton$key } from '@atlassian/jira-relay/src/__generated__/OpenTemplatesPanelButton.graphql';
import {
	PANEL_ID,
	SIDEBAR_WIDTH,
} from '@atlassian/jira-projects-directory-templates-sidebar/src/constants.tsx';
import { useSetProjectListRightPanelState } from '@atlassian/jira-projects-directory-templates-sidebar/src/services/set-right-panel-state/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './messages.tsx';

interface Props {
	dataRef: OpenTemplatesPanelButton$key;
}

const OpenTemplatesPanelButton = ({ dataRef }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'directoryHeader',
	});

	const rightSidebarCurrentState = useActiveRightSidebarState();
	const { openRightSidebar } = useRightSidebarController();
	const { formatMessage } = useIntl();
	const { canCreateProject, userPreferences, sidebarExperimentTemplates, shouldDisplay } =
		useFragment<OpenTemplatesPanelButton$key>(
			graphql`
				fragment OpenTemplatesPanelButton on JiraQuery
				@argumentDefinitions(
					isAllowingEndUsersToAccessCustomTemplates: {
						type: "Boolean!"
						provider: "@atlassian/jira-relay-provider/src/jira-custom-template-allow-end-users-to-create-custom-templates.relayprovider"
					}
				) {
					canCreateProject: canPerform(type: CREATE_PROJECT, cloudId: $cloudId)
						@optIn(to: "JiraAction")
					shouldDisplay: canPerform(type: VIEW_PROJECT_CREATION_ENTRY, cloudId: $cloudId)
						@include(if: $isAllowingEndUsersToAccessCustomTemplates)
						@optIn(to: "JiraAction")
					userPreferences(cloudId: $cloudId)
						@optIn(to: "JiraUserPreferences")
						@required(action: THROW) {
						__id
						projectListRightPanelState @optIn(to: "JiraProjectListRightPanelState")
					}
					sidebarExperimentTemplates: projectListViewTemplates(
						cloudId: $cloudId
						experimentKey: "sidebarExperiment"
					) @optIn(to: "JiraProjectListViewTemplate") @required(action: THROW) {
						totalCount @required(action: THROW)
					}
				}
			`,
			dataRef,
		);

	const setProjectListRightPanelState = useSetProjectListRightPanelState(userPreferences.__id);

	const onClick = useCallback(() => {
		fireUIAnalytics(analyticsEvent, 'openTemplatesPanelButton');

		if (
			userPreferences.projectListRightPanelState === 'OPEN' &&
			rightSidebarCurrentState?.panelId !== PANEL_ID
		) {
			openRightSidebar(PANEL_ID, SIDEBAR_WIDTH, false, true);
			return;
		}

		setProjectListRightPanelState(
			userPreferences.projectListRightPanelState === 'OPEN' ? 'CLOSED' : 'OPEN',
		);
	}, [
		analyticsEvent,
		openRightSidebar,
		rightSidebarCurrentState?.panelId,
		setProjectListRightPanelState,
		userPreferences.projectListRightPanelState,
	]);
	const shouldDisplayCreateProject = fg('allow_end_users_to_access_custom_templates')
		? shouldDisplay
		: canCreateProject;

	if (
		!shouldDisplayCreateProject ||
		typeof sidebarExperimentTemplates?.totalCount !== 'number' ||
		sidebarExperimentTemplates.totalCount === 0
	) {
		// Don't show the button if the user can't create a project or if there are no templates recommended.
		return null;
	}

	return (
		<>
			<Button
				data-testId="global-pages.directories.projects-directory-v3.templates-panel-button"
				isSelected={rightSidebarCurrentState?.panelId === PANEL_ID}
				onClick={onClick}
			>
				{formatMessage(messages.templatesButton)}
			</Button>
			<FireScreenAnalytics
				attributes={{
					experiment: 'recommendationsinProjectsDirectoryWithHoverPreview',
					totalCount: sidebarExperimentTemplates.totalCount,
					canCreateProject,
				}}
			/>
		</>
	);
};

const OpenTemplatesPanelButtonContainer = (props: Props): React.JSX.Element => (
	<JSErrorBoundary
		id="open-templates-panel-button"
		packageName="jiraProjectsDirectoryV3"
		fallback="unmount"
	>
		<ContextualAnalyticsData sourceName="projectsV3" sourceType={SCREEN}>
			<OpenTemplatesPanelButton {...props} />
		</ContextualAnalyticsData>
	</JSErrorBoundary>
);

export { OpenTemplatesPanelButtonContainer as OpenTemplatesPanelButton };
